/**
 * TA 的性别与生日（PET-FACT-01，合同 docs/coordination/PET-FACT-01-CONTRACT-I-2026-09-29.md 第 1、2、4 节）：表单模型与说法。
 * - 出生日期的写法（birth_label）和岁数（age）只由服务端算（合同第 2 节“只有一处实现”），前端不自己拼、不自己算。
 * - 这里只做三件事：表单与回包互转；提前挡住合同第 1 节的组合规则（服务端照样校验，前端挡只是少走一趟）；给玩家看的几句话。
 * - “晚于今天”只挡明显超过今年的年份；精确到月日的判断交给服务端（它按 UTC+14 的今天判），回 422 时照说（I 2026-09-30）。
 * - 不从名字、照片、花色、性格推断任何一项；旧宠、没填、清空都是“不确定 / 不知道”。
 */
import type { PetAge, PetBirth, PetCoreFactsUpdate, PetCoreFactsView, PetSex } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";

export interface FactsForm {
  sex: PetSex;
  year: number | null;
  month: number | null;
  day: number | null;
  /** 年份是估计的（显示为“约”）；只在有年份时有意义 */
  estimated: boolean;
  chosenMonth: number | null;
  chosenDay: number | null;
}

export const EMPTY_FACTS: FactsForm = { sex: "unknown", year: null, month: null, day: null, estimated: false, chosenMonth: null, chosenDay: null };

/** 玩家界面统一叫“男孩 / 女孩 / 不确定”（合同第 1 节）。 */
export const SEX_TEXT: Record<PetSex, string> = { male: "男孩", female: "女孩", unknown: "不确定" };
export const SEX_CHOICES: readonly PetSex[] = ["male", "female", "unknown"];

export const MIN_BIRTH_YEAR = 1900;

export function formFromView(view: PetCoreFactsView): FactsForm {
  return {
    sex: view.sex,
    year: view.birth.year,
    month: view.birth.month,
    day: view.birth.day,
    estimated: view.birth.year !== null && view.birth.estimated,
    chosenMonth: view.chosen_birthday?.month ?? null,
    chosenDay: view.chosen_birthday?.day ?? null,
  };
}

/** 整体保存的请求体：三组都带；“估计”只在有年份时为真；选定生日月日不全就当没选。 */
export function updateFrom(form: FactsForm, expectedVersion: number): PetCoreFactsUpdate {
  return {
    sex: form.sex,
    birth: { year: form.year, month: form.month, day: form.day, estimated: form.year !== null && form.estimated },
    chosen_birthday: form.chosenMonth !== null && form.chosenDay !== null ? { month: form.chosenMonth, day: form.chosenDay } : null,
    expected_version: expectedVersion,
  };
}

/** 三项都没填（不确定、不知道、没选）。 */
export function isEmptyFacts(form: FactsForm): boolean {
  return sameFacts(form, EMPTY_FACTS);
}

/** 两份表单保存出去是不是同一份（比请求体，不比版本号）。 */
export function sameFacts(a: FactsForm, b: FactsForm): boolean {
  return JSON.stringify(updateFrom(a, 0)) === JSON.stringify(updateFrom(b, 0));
}

/**
 * 从来没存过（version=0）：卡片写“还没填”，不列“不确定 / 不知道”几行；存过、选的就是不确定或不知道，才照列
 * （2531 ADVISORY-R14 第 1 条，I 2026-09-30）。
 */
export function neverSaved(view: PetCoreFactsView): boolean {
  return view.version === 0;
}

export type FactGroup = "sex" | "birth" | "chosen";
const GROUP_SOURCE = { sex: "sex_source", birth: "birth_source", chosen: "chosen_source" } as const;

/** 值来源的轻标：领养来的原创居民由平台设定（platform_original）时标“星球设定”；主人确认的、原型核实的不标。 */
export function sourceTag(view: PetCoreFactsView, group: FactGroup): string | null {
  return view[GROUP_SOURCE[group]] === "platform_original" ? "星球设定" : null;
}

/** 这次改动碰到了哪些“星球设定”的组：保存前要提醒一句“会和 TA 过去的设定不一样”（2531 第 3 条）。 */
export function changedPlatformGroups(base: PetCoreFactsView, form: FactsForm): FactGroup[] {
  const before = updateFrom(formFromView(base), 0);
  const after = updateFrom(form, 0);
  const changed: Record<FactGroup, boolean> = {
    sex: before.sex !== after.sex,
    birth: JSON.stringify(before.birth) !== JSON.stringify(after.birth),
    chosen: JSON.stringify(before.chosen_birthday) !== JSON.stringify(after.chosen_birthday),
  };
  return (["sex", "birth", "chosen"] as const).filter((group) => changed[group] && base[GROUP_SOURCE[group]] === "platform_original");
}

/** “只知道大概几岁？”能选的岁数：0（不到 1 岁）到 30 岁。 */
export const APPROX_AGES = Array.from({ length: 31 }, (_, n) => n);
export function approxAgeLabel(n: number): string {
  return n === 0 ? "不到 1 岁" : `约 ${n} 岁`;
}
/**
 * TA 家那边的“今年”：按家的时区取当地日期（Intl），不用浏览器所在时区（c84a 1715Z：跨年那几个小时最容易差一年）。
 * 没给时区、或不认识这个时区时为 null。
 */
export function homeYear(timeZone: string | null | undefined, now: Date = new Date()): number | null {
  if (!timeZone) return null;
  try {
    const year = Number(new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric" }).format(now));
    return Number.isFinite(year) ? year : null;
  } catch {
    return null;
  }
}

/**
 * 换算“约 N 岁”用的今年：只用 TA 家那边的今年。家的时区未知或认不出时为 null——不借浏览器时区，也不拿 UTC+14 猜
 * （c84a 1745Z PET-FACT-01：UTC+14 只能当“明显未来年份”的宽松上界，不能当未知家庭的事实日期；跨年那几个小时会差一年）。
 */
export function approxBaseYear(timeZone: string | null | undefined, now: Date = new Date()): number | null {
  return homeYear(timeZone, now);
}

/**
 * 选了“约 N 岁”：出生年份换算成“TA 家那边的今年 − N”，并标为估计（只给年份，不偷填月日；显示的岁数照旧用服务端算的）。
 * 家的今年不知道时不换算，表单原样返回（页面这时也不给这条路，让主人直接选年份、勾“估计”）。
 */
export function withApproxAge(form: FactsForm, n: number, now: Date = new Date(), timeZone: string | null = null): FactsForm {
  const base = approxBaseYear(timeZone, now);
  return base === null ? form : { ...form, year: base - n, estimated: true };
}
/**
 * 表单现在的出生年份对应“大概几岁”：年份和岁数是同一件事的两个入口，双向联动——改岁数年份跟着变（withApproxAge），
 * 改年份岁数跟着变（这里按年份现算，不另存一份），不会出现“2019 年、约 5 岁”这种对不上的组合（2531 / I 2026-09-30）。
 * 年份不知道、超出能选的范围、或家的今年不知道时为 null。
 */
export function approxAgeOf(form: FactsForm, now: Date = new Date(), timeZone: string | null = null): number | null {
  const base = approxBaseYear(timeZone, now);
  if (form.year === null || base === null) return null;
  const n = base - form.year;
  return n >= 0 && n <= APPROX_AGES[APPROX_AGES.length - 1] ? n : null;
}

function isLeap(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** 这一月有几天；不知道年份时 2 月按 29 天（只有月日时 2 月 29 日照收，合同第 1 节）。 */
export function daysInMonth(month: number, year: number | null): number {
  if (month === 2) return year === null || isLeap(year) ? 29 : 28;
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

/** “今年”取全世界最早到的那个时区（UTC+14）：只拿来挡明显超过今年的年份、定年份下拉的上限，不做月日的精确判断，也不拿来换算“约 N 岁”。 */
export function latestYear(now: Date = new Date()): number {
  return new Date(now.getTime() + 14 * 3_600_000).getUTCFullYear();
}

export type FactsProblem = "day_missing_month" | "month_only" | "estimate_needs_year" | "year_out_of_range" | "date_invalid" | "chosen_incomplete" | "chosen_invalid";

/** 合同第 1 节会被拒的组合（按显示顺序）。 */
export function factsProblems(form: FactsForm, now: Date = new Date()): FactsProblem[] {
  const out: FactsProblem[] = [];
  const { year, month, day } = form;
  if (day !== null && month === null) out.push("day_missing_month");
  if (month !== null && year === null && day === null) out.push("month_only");
  if (form.estimated && year === null) out.push("estimate_needs_year");
  if (year !== null && (year < MIN_BIRTH_YEAR || year > latestYear(now))) out.push("year_out_of_range");
  if (month !== null && day !== null && day > daysInMonth(month, year)) out.push("date_invalid");
  if ((form.chosenMonth === null) !== (form.chosenDay === null)) out.push("chosen_incomplete");
  else if (form.chosenMonth !== null && form.chosenDay !== null && form.chosenDay > daysInMonth(form.chosenMonth, null)) out.push("chosen_invalid");
  return out;
}

/** 提前挡住时说的话：说清缺什么、怎么改。 */
export function problemText(problem: FactsProblem, form: FactsForm): string {
  switch (problem) {
    case "day_missing_month":
      return "选了日子，也要选上月份。";
    case "month_only":
      return "只知道月份还不够：再选上日子，或者把月份改回“不知道”。";
    case "estimate_needs_year":
      return "“估计”只用在年份上，先选年份。";
    case "year_out_of_range":
      return `年份要在 ${MIN_BIRTH_YEAR} 年到今年之间。`;
    case "date_invalid":
      return form.year !== null && form.month === 2 && form.day === 29 ? `${form.year}年没有2月29日，换一天。` : `${form.month}月没有${form.day}日，换一天。`;
    case "chosen_incomplete":
      return "为 TA 选定的生日要月和日都选上，或者都不选。";
    case "chosen_invalid":
      return `${form.chosenMonth}月没有${form.chosenDay}日，给 TA 选定的生日换一天。`;
  }
}

/** 岁数：只在服务端给了 age 时出；0 岁写“不到 1 岁”（估计的也一样，不写“约不到 1 岁”，I 2026-09-30）。 */
export function ageText(age: PetAge | null): string | null {
  if (!age) return null;
  if (age.years <= 0) return "不到 1 岁";
  return age.approximate ? `约 ${age.years} 岁` : `${age.years} 岁`;
}

/** 为 TA 选定的生日（月日）：单独一行，和出生日期分开说。 */
export function chosenText(view: PetCoreFactsView): string | null {
  return view.chosen_birthday ? `${view.chosen_birthday.month}月${view.chosen_birthday.day}日` : null;
}

/** 家人在你读取之后改过（409 VERSION_CONFLICT，details.reason = facts_version_conflict）。 */
export function isFactsVersionConflict(error: unknown): boolean {
  return isApiError(error) && error.status === 409 && (error.code === "VERSION_CONFLICT" || error.details?.reason === "facts_version_conflict");
}

/** 这次用的保存键被服务端判为用错了（同一个键配了不同的内容，409 IDEMPOTENCY_KEY_REUSED）：这个键作废，下次保存换新键。 */
export function isFactsKeyRejected(error: unknown): boolean {
  return isApiError(error) && error.code === "IDEMPOTENCY_KEY_REUSED";
}

/**
 * 现在记的和这次填的对不上的一项（CREATE-PET-RED-01，I 10-09）：性别“男孩 / 女孩 / 还不知道”，选定的生日没选写“没选”；
 * 出生日期：现在记的照服务端的写法（birth_label，不知道写“不知道”）；这次填的还没存上，照主人填的原样写——年、月、日、估计这几样拼一句
 * （“2019 年（估计）”“2019 年 5 月”“5 月 12 日”），不套服务端的写法：出生日期的写法只有服务端一处（合同第 2 节）。
 */
export interface FactsDifference {
  label: string;
  now: string;
  draft: string;
}

/** 只列有差别的项，按性别、出生日期、为 TA 选定的生日的顺序；“估计”只在有年份时算数。不比版本号。 */
export function factsDifferences(stored: PetCoreFactsView, body: PetCoreFactsUpdate): FactsDifference[] {
  const sexText = (sex: PetSex) => (sex === "unknown" ? "还不知道" : SEX_TEXT[sex]);
  const birthKey = (birth: PetBirth) => [birth.year, birth.month, birth.day, birth.year !== null && birth.estimated].join("/");
  const chosen = (value: PetCoreFactsUpdate["chosen_birthday"]) => (value ? `${value.month}月${value.day}日` : "没选");
  // 这次填的出生日期：照主人填的原样拼（“估计”只修饰年份），什么都没填写“不知道”
  const filled = (birth: PetBirth) => {
    const parts = [birth.year !== null ? `${birth.year} 年` : null, birth.month !== null ? `${birth.month} 月` : null, birth.day !== null ? `${birth.day} 日` : null].filter(Boolean);
    return parts.length ? `${parts.join(" ")}${birth.year !== null && birth.estimated ? "（估计）" : ""}` : "不知道";
  };
  const out: FactsDifference[] = [];
  if (stored.sex !== body.sex) out.push({ label: "性别", now: sexText(stored.sex), draft: sexText(body.sex) });
  if (birthKey(stored.birth) !== birthKey(body.birth)) out.push({ label: "出生日期", now: stored.birth_label ?? "不知道", draft: filled(body.birth) });
  if (chosen(stored.chosen_birthday) !== chosen(body.chosen_birthday)) out.push({ label: "为 TA 选定的生日", now: chosen(stored.chosen_birthday), draft: chosen(body.chosen_birthday) });
  return out;
}

/** 服务端现在存着的就是这份请求体要存的：一项都没差别（CREATE-PET-RED-01）。 */
export function storedFactsMatch(view: PetCoreFactsView, body: PetCoreFactsUpdate): boolean {
  return factsDifferences(view, body).length === 0;
}

/**
 * 保存回 409 以后怎么认（CREATE-PET-RED-01，I 10-09 裁定第 3 条；后端 FACTS-ORIGINAL-01 打开 expose_original）。建宠页补生日和档案卡共用：
 * - saved：409 带了当初那次的回包（details.original，回执丢了、同一个键再发），或者重新读一次、存着的就是这次要存的——都算存好了，用它刷新；
 * - busy：当初那次还在处理（409 IDEMPOTENCY_IN_PROGRESS，或同键换了内容时 details.original_state = in_progress）——说“还在保存，稍等一下再看”，
 *   不自己重发、不读；主人点“再看看”（档案卡再点）才原样重发同键同体，结果照样交给这里认；
 * - refused：都没带、读到的版本也没动（谁都没存，只是这次没收下，例如键被判用错了）——照常说没存上；
 * - conflict：读到的变成了别的——两边并排写出来，等主人选；覆盖用 details.current_version，没给就用读到的版本。
 * 不是这几种 409（还在处理、键用错了、版本对不上）就是 null。read 只在 409 既没带回包、也没说还在处理时才调；读不到就把那次的错抛出去。
 */
export type FactsSettle =
  | { kind: "saved"; view: PetCoreFactsView }
  | { kind: "busy" }
  | { kind: "refused" }
  | { kind: "conflict"; stored: PetCoreFactsView; version: number };
export async function settleFactsConflict(error: unknown, body: PetCoreFactsUpdate, read: () => Promise<PetCoreFactsView>): Promise<FactsSettle | null> {
  if (isFactsBusy(error)) return { kind: "busy" };
  if (!isFactsKeyRejected(error) && !isFactsVersionConflict(error)) return null;
  const details: Record<string, unknown> = (isApiError(error) ? error.details : null) ?? {};
  if (details.original && typeof details.original === "object") return { kind: "saved", view: details.original as PetCoreFactsView };
  const stored = await read();
  if (storedFactsMatch(stored, body)) return { kind: "saved", view: stored };
  if (stored.version === body.expected_version) return { kind: "refused" };
  const latest = details.current_version;
  return { kind: "conflict", stored, version: typeof latest === "number" ? latest : stored.version };
}

/** 当初那次还在处理：409 IDEMPOTENCY_IN_PROGRESS（同键同内容），或同键换了内容时 details.original_state = in_progress（CREATE-PET-RED-01）。 */
export function isFactsBusy(error: unknown): boolean {
  return isApiError(error) && (error.code === "IDEMPOTENCY_IN_PROGRESS" || (error.code === "IDEMPOTENCY_KEY_REUSED" && error.details?.original_state === "in_progress"));
}
/** 还在处理时那一句：建宠页、档案卡都放在常驻的播报区里礼貌播报 */
export const FACTS_BUSY_TEXT = "还在保存，稍等一下再看。";

/** 服务端 422 的原因码（合同第 4 节）→ 人话；表里没有的走通用那句。 */
export const FACT_REASON_TEXT: Record<string, string> = {
  facts_date_invalid: "这个日期不存在，再看一下月和日。",
  facts_date_in_future: "这个日子还没到，再看一下。",
  facts_precision_invalid: "只知道日子的话要连月份一起填；只有月份还不够。",
  facts_estimate_needs_year: "“估计”只用在年份上，先选年份。",
  facts_year_out_of_range: `年份要在 ${MIN_BIRTH_YEAR} 年到今年之间。`,
  facts_chosen_birthday_invalid: "为 TA 选定的生日这一天不存在，再看一下。",
};

/** 没保存上时的一句话（版本冲突另有处理，不走这里）。 */
export function factsSaveFailureText(error: unknown): string {
  if (!isApiError(error)) return "没保存上，再试一次。";
  if (error.kind === "network" || error.kind === "timeout") return "信号断了一下，没保存上，再试一次。";
  if (error.isAuth) return "登录状态过期了，重新登录后再保存。";
  if (error.status === 403) return "只有家庭管理员能改这一项。你现在可能已经不是管理员了。";
  // 服务端那边出错（5xx）：不照搬“请求未能完成”这类原话
  if (error.status !== null && error.status >= 500) return "服务那边一时没接上，稍后再试。";
  // 保存键的两种 409：后端原话是“同一个 Idempotency-Key 被用于不同的请求”“相同请求正在处理中”这类开发说法，不照搬
  // 还在处理（409 IDEMPOTENCY_IN_PROGRESS，或同键换了内容时 original_state = in_progress）：统一说“还在保存，稍等一下再看”（CREATE-PET-RED-01，I 10-09）
  if (isFactsBusy(error)) return FACTS_BUSY_TEXT;
  if (isFactsKeyRejected(error)) return "没保存上，再点一次保存。";
  const reason = typeof error.details?.reason === "string" ? error.details.reason : null;
  if (reason && Object.prototype.hasOwnProperty.call(FACT_REASON_TEXT, reason)) return FACT_REASON_TEXT[reason];
  if (error.code === "VALIDATION_FAILED") return "有一项填得不对，再看一下。";
  return error.playerMessage || "没保存上，再试一次。";
}
